import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Eye, ShieldAlert, X, Users, ArrowLeft, Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Case-insensitive A→Z sort by company name with fallback to person name.
// ceo_instruction_ref: alphabetical_sort_all_lists_28jun2026
const byCompanyAZ = (a, b) => {
  const ax = (a.salon_name || a.company_name || a.name || a.customer_name || "").trim().toLowerCase();
  const bx = (b.salon_name || b.company_name || b.name || b.customer_name || "").trim().toLowerCase();
  return ax.localeCompare(bx, "en", { sensitivity: "base" });
};

// BUILD 4 Part A (view_as_banner_name_30jul2026): a name ALWAYS renders —
// fall back name → salon → company → first+last → email → user_id.
// view_as_picker_fix_02sep2026: registrations-list fallback rule —
// salon name, else person name, else email. Never a bare dash.
const pickerPrimaryName = (u) => (
  (u?.salon_name || '').trim()
  || (u?.name || '').trim()
  || `${u?.first_name || ''} ${u?.last_name || ''}`.trim()
  || (u?.email || '').trim()
  || u?.user_id
);

// Country (NOT region) → flag. Unknown/absent country → no flag.
const COUNTRY_ISO2 = {
  'united kingdom': 'GB', 'uk': 'GB', 'great britain': 'GB',
  'england': 'GB', 'scotland': 'GB', 'wales': 'GB',
  'northern ireland': 'GB', 'nigeria': 'NG', 'kenya': 'KE',
  'ghana': 'GH', 'gambia': 'GM', 'ireland': 'IE',
  'united states': 'US', 'usa': 'US',
};
const countryFlag = (country) => {
  const key = (country || '').trim().toLowerCase();
  const iso = COUNTRY_ISO2[key]
    || (/^[a-z]{2}$/.test(key) ? key.toUpperCase() : null);
  if (!iso) return null;
  return iso.replace(/./g,
    (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));
};

const resolveDisplayName = (u) => (
  (u?.name || '').trim()
  || (u?.salon_name || '').trim()
  || (u?.company_name || '').trim()
  || `${u?.first_name || ''} ${u?.last_name || ''}`.trim()
  || (u?.email || '').trim()
  || u?.user_id
  || 'Unknown account'
);

/**
 * ViewAsSelector - Shown in the sidebar for Founder/CEO only.
 * Allows switching to see the platform as any user/role.
 */
export function ViewAsSelector() {
  const { user, token, setImpersonation, impersonation } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [mode, setMode] = useState('view');
  const [loading, setLoading] = useState(false);

  const canImpersonate = user?.role === 'founder' || user?.role === 'super_admin' || user?.role === 'admin';

  const fetchUsers = useCallback(async () => {
    if (!canImpersonate || impersonation) return;
    try {
      const res = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.filter(u => u.user_id !== user.user_id));
    } catch { /* ignore */ }
  }, [token, canImpersonate, user?.user_id, impersonation]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Fix 1 (30 May 2026,
  //   ceo_instruction_ref:
  //     founder_override_ghost_user_dropdown_30may2026)
  // — refetch on dialog open so the dropdown always reflects
  // the current db.users (single source of truth) rather
  // than a stale React-state snapshot from mount-time.
  useEffect(() => {
    if (showDialog) fetchUsers();
  }, [showDialog, fetchUsers]);

  if (!canImpersonate || impersonation) return null;

  const handleViewAs = async (directUserId = null) => {
    const targetId = directUserId || selectedUserId;
    if (!targetId) { toast.error('Select a user'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/founder/view-as`, {
        user_id: targetId,
        mode,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setImpersonation({
        token: res.data.token,
        user: res.data.user,
        mode: res.data.mode,
        originalToken: token,
        originalUser: user,
        impersonatedByName: res.data.impersonated_by_name,
      });

      setShowDialog(false);
      toast.success(`Now ${mode === 'act' ? 'acting' : 'viewing'} as ${resolveDisplayName(res.data.user)} (${res.data.user.role})`);

      // Navigate to dashboard to load the correct role-based view
      navigate('/dashboard');
    } catch (err) {
      // Fix 2 — graceful handling for ghost users that no
      // longer exist in db.users (e.g. deleted by Stage 18
      // consolidation). Strip the offending entry from the
      // local list, clear the selection, close the modal
      // cleanly, and surface a clear message. No crash, no
      // reload loop.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 404 || /user not found/i.test(detail)) {
        const ghostId = selectedUserId;
        setUsers(prev => prev.filter(u => u.user_id !== ghostId));
        setSelectedUserId('');
        setShowDialog(false);
        toast.error('This account no longer exists. The dropdown has been refreshed.');
        // Force a clean re-fetch so any other stale rows are
        // also dropped on the same gesture.
        fetchUsers();
      } else {
        toast.error(detail || 'Failed to switch view');
      }
    }
    setLoading(false);
  };

  // Group users by role
  const grouped = {};
  users.forEach(u => {
    const role = u.role || 'other';
    if (!grouped[role]) grouped[role] = [];
    grouped[role].push(u);
  });

  return (
    <>
      <button
        onClick={() => setShowDialog(true)}
        className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors w-full"
        data-testid="view-as-trigger"
      >
        <Eye className="w-3.5 h-3.5" />
        <span className="uppercase tracking-wider font-medium">View As</span>
      </button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="founder-override-dialog">
          <DialogHeader className="min-w-0">
            <DialogTitle className="font-serif text-xl">Founder Override</DialogTitle>
            <DialogDescription>View or act as any user on the platform. All actions are logged.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4 min-w-0">
            {/* Mode Selection */}
            <div className="flex gap-2" data-testid="override-mode-selector">
              <button
                onClick={() => setMode('view')}
                className={`flex-1 p-3 border rounded-sm text-center transition-colors ${mode === 'view' ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-border/40 text-muted-foreground'}`}
                data-testid="mode-view"
              >
                <Eye className="w-4 h-4 mx-auto mb-1" />
                <p className="text-xs font-semibold uppercase tracking-wider">View As</p>
                <p className="text-[10px] mt-1">See what they see. Read only.</p>
              </button>
              <button
                onClick={() => setMode('act')}
                className={`flex-1 p-3 border rounded-sm text-center transition-colors ${mode === 'act' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-border/40 text-muted-foreground'}`}
                data-testid="mode-act"
              >
                <ShieldAlert className="w-4 h-4 mx-auto mb-1" />
                <p className="text-xs font-semibold uppercase tracking-wider">Act As</p>
                <p className="text-[10px] mt-1">Full actions. Extra logged.</p>
              </button>
            </div>

            {/* User Selection — searchable, grouped by role.
                view_as_picker_fix_02sep2026 */}
            <Command
              className="border border-border/40 rounded-sm"
              data-testid="view-as-user-select"
            >
              <CommandInput
                placeholder="Search salon, name or email..."
                className="text-base"
                data-testid="view-as-search-input"
              />
              <CommandList className="max-h-56 overflow-y-auto">
                <CommandEmpty>No accounts match.</CommandEmpty>
                {Object.entries(grouped).map(([role, roleUsers]) => (
                  <CommandGroup
                    key={role}
                    heading={role.replace(/_/g, ' ')}
                  >
                    {roleUsers.slice().sort(byCompanyAZ).map(u => {
                      const flag = countryFlag(u.country);
                      const primary = pickerPrimaryName(u);
                      const person = (u.name || '').trim();
                      const secondary =
                        (u.salon_name || '').trim() && person
                        && person !== primary ? person : '';
                      return (
                        <CommandItem
                          key={u.user_id}
                          value={`${u.salon_name || ''} ${u.name || ''} ${u.email || ''} ${u.user_id}`}
                          onSelect={() => {
                            // tap_to_go_10sep2026: tapping a name
                            // selects and enters immediately in the
                            // chosen mode; confirm button unchanged.
                            setSelectedUserId(u.user_id);
                            handleViewAs(u.user_id);
                          }}
                          className={selectedUserId === u.user_id ? 'bg-[#B99A65]/15' : ''}
                          data-testid={`view-as-option-${u.user_id}`}
                        >
                          {flag && (
                            <span className="mr-1.5 shrink-0" aria-hidden="true">{flag}</span>
                          )}
                          <span className="truncate min-w-0">{primary}</span>
                          {secondary && (
                            <span className="ml-1.5 text-xs text-muted-foreground truncate min-w-0">{secondary}</span>
                          )}
                          {selectedUserId === u.user_id && (
                            <Check className="w-3.5 h-3.5 ml-auto shrink-0 text-[#B99A65]" />
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
            {selectedUserId && (
              <p className="text-xs text-muted-foreground" data-testid="view-as-selected-label">
                Selected: {pickerPrimaryName(users.find(u => u.user_id === selectedUserId) || {})}
              </p>
            )}

            {/* Confirm */}
            {mode === 'act' && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-sm text-xs text-amber-800">
                <ShieldAlert className="w-3.5 h-3.5 inline mr-1" />
                Act As mode allows full actions on behalf of this user. All actions will be logged to the audit trail.
              </div>
            )}

            <Button
              onClick={() => handleViewAs()}
              disabled={!selectedUserId || loading}
              className="w-full h-10 rounded-sm bg-[#B99A65] text-white hover:bg-[#7E5F2E] text-xs uppercase tracking-widest"
              data-testid="confirm-view-as"
            >
              {loading ? 'Switching...' : `${mode === 'view' ? 'View' : 'Act'} As Selected User`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * ImpersonationBanner - Shown at the top of the page when impersonating.
 */
export function ImpersonationBanner() {
  const { impersonation, exitImpersonation } = useAuth();
  const navigate = useNavigate();

  if (!impersonation) return null;

  const isAct = impersonation.mode === 'act';

  const handleExit = () => {
    exitImpersonation();
    navigate('/dashboard');
  };

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 px-4 py-2 flex items-center justify-between ${isAct ? 'bg-amber-500 text-white' : 'bg-blue-500 text-white'}`}
      data-testid="impersonation-banner">
      <div className="flex items-center gap-3">
        {isAct ? <ShieldAlert className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        <span className="text-xs font-semibold uppercase tracking-wider">
          {isAct ? 'ACT AS' : 'VIEW AS'}: {resolveDisplayName(impersonation.user)} ({impersonation.user?.role?.replace('_', ' ')})
        </span>
        <Badge className="text-[9px] bg-white/20 text-white rounded-sm">
          by {impersonation.impersonatedByName}
        </Badge>
      </div>
      <Button
        onClick={handleExit}
        variant="ghost"
        className="h-7 text-white hover:bg-white/20 text-xs uppercase tracking-wider"
        data-testid="exit-impersonation"
      >
        <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Return to {impersonation.originalUser?.name}
      </Button>
    </div>
  );
}
